import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Heart, Lock, Unlock, Image, Star, Trophy, 
  Sparkles, Download, Share2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianGallery = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState({ total: 10, unlocked: 0, favorites: 0, completion_percentage: 0 });
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, [childId]);

  const fetchGallery = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/gallery/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGallery(response.data.gallery);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (week, currentlyFavorite) => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      
      if (currentlyFavorite) {
        await axios.delete(`${API_URL}/api/christian/gallery/${childId}/favorite/${week}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Removed from favorites');
      } else {
        const response = await axios.post(
          `${API_URL}/api/christian/gallery/${childId}/favorite/${week}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.xp_awarded > 0) {
          toast.success(`Added to collection! +${response.data.xp_awarded} XP`, { icon: '⭐' });
        } else {
          toast.success('Added to favorites!', { icon: '❤️' });
        }
        
        if (response.data.new_badge) {
          setTimeout(() => {
            toast.success(`New Badge: ${response.data.new_badge.name} ${response.data.new_badge.icon}`, {
              duration: 5000
            });
          }, 1000);
        }
      }
      
      // Refresh gallery
      fetchGallery();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error(error.response?.data?.detail || 'Failed to update favorites');
    }
  };

  const openViewer = (item) => {
    if (!item.unlocked) {
      toast.info(`Complete Week ${item.week} lesson to unlock this artwork!`, { icon: '🔒' });
      return;
    }
    setSelectedImage(item);
    setViewerOpen(true);
  };

  const navigateViewer = (direction) => {
    const currentIndex = gallery.findIndex(g => g.week === selectedImage.week);
    let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    
    // Find next unlocked image
    while (newIndex >= 0 && newIndex < gallery.length) {
      if (gallery[newIndex].unlocked) {
        setSelectedImage(gallery[newIndex]);
        return;
      }
      newIndex += direction === 'next' ? 1 : -1;
    }
  };

  const downloadImage = (url, title) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-md border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/christian/child/${childId}`)}
                className="text-white hover:bg-purple-500/20"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Image className="h-5 w-5 text-purple-400" />
                  Illustration Gallery
                </h1>
                <p className="text-sm text-purple-300">Collect beautiful Bible story artwork</p>
              </div>
            </div>
            
            {/* Collection Stats */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{stats.unlocked}/{stats.total}</div>
                <div className="text-xs text-purple-300">Unlocked</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-400">{stats.favorites}</div>
                <div className="text-xs text-purple-300">Favorites</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Card */}
        <Card className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-purple-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-400" />
                <span className="text-white font-medium">Collection Progress</span>
              </div>
              <span className="text-purple-200 text-sm">{stats.completion_percentage}% Complete</span>
            </div>
            <Progress value={stats.completion_percentage} className="h-3 bg-purple-900/50" />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-green-400">
                  <Unlock className="h-4 w-4" />
                  <span>{stats.unlocked} Unlocked</span>
                </div>
                <div className="flex items-center gap-1 text-pink-400">
                  <Heart className="h-4 w-4 fill-current" />
                  <span>{stats.favorites} Favorites</span>
                </div>
              </div>
              {stats.completion_percentage === 100 && (
                <Badge className="bg-yellow-500 text-yellow-900">
                  <Star className="h-3 w-3 mr-1 fill-current" />
                  Complete!
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Badge Progress */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className={`border-purple-500/30 ${stats.favorites >= 5 ? 'bg-purple-600/30' : 'bg-slate-800/50'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`text-3xl ${stats.favorites >= 5 ? '' : 'grayscale opacity-50'}`}>🖼️</div>
              <div>
                <div className="text-white font-medium text-sm">Art Collector</div>
                <div className="text-purple-300 text-xs">{Math.min(stats.favorites, 5)}/5 favorites</div>
                <Progress value={(Math.min(stats.favorites, 5) / 5) * 100} className="h-1 mt-1 bg-purple-900/50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`border-purple-500/30 ${stats.favorites >= 10 ? 'bg-pink-600/30' : 'bg-slate-800/50'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`text-3xl ${stats.favorites >= 10 ? '' : 'grayscale opacity-50'}`}>🎨</div>
              <div>
                <div className="text-white font-medium text-sm">Master Collector</div>
                <div className="text-purple-300 text-xs">{Math.min(stats.favorites, 10)}/10 favorites</div>
                <Progress value={(Math.min(stats.favorites, 10) / 10) * 100} className="h-1 mt-1 bg-pink-900/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          <AnimatePresence>
            {gallery.map((item, index) => (
              <motion.div
                key={item.week}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`group cursor-pointer transition-all duration-300 overflow-hidden border-2 ${
                    item.unlocked 
                      ? item.favorite 
                        ? 'border-pink-500 bg-pink-500/10' 
                        : 'border-purple-500/30 bg-slate-800/50 hover:border-purple-400'
                      : 'border-slate-700 bg-slate-800/30'
                  }`}
                  onClick={() => openViewer(item)}
                  data-testid={`gallery-item-${item.week}`}
                >
                  <div className="aspect-square relative overflow-hidden">
                    {item.unlocked ? (
                      <>
                        <img 
                          src={item.illustration} 
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(item.week, item.favorite);
                          }}
                          className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
                            item.favorite 
                              ? 'bg-pink-500 text-white' 
                              : 'bg-black/50 text-white hover:bg-pink-500'
                          }`}
                          data-testid={`favorite-btn-${item.week}`}
                        >
                          <Heart className={`h-4 w-4 ${item.favorite ? 'fill-current' : ''}`} />
                        </button>
                        
                        {/* Week Badge */}
                        <div className="absolute top-2 left-2 bg-purple-600/90 text-white text-xs px-2 py-1 rounded-full">
                          Week {item.week}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-slate-700/50 flex flex-col items-center justify-center">
                        <Lock className="h-8 w-8 text-slate-500 mb-2" />
                        <span className="text-slate-400 text-xs">Week {item.week}</span>
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-3">
                    <h3 className={`font-medium text-sm truncate ${item.unlocked ? 'text-white' : 'text-slate-500'}`}>
                      {item.title}
                    </h3>
                    <p className={`text-xs truncate ${item.unlocked ? 'text-purple-300' : 'text-slate-600'}`}>
                      {item.scripture}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Unlock More CTA */}
        {stats.unlocked < stats.total && (
          <Card className="mt-6 bg-gradient-to-r from-indigo-600/30 to-purple-600/30 border-indigo-500/30">
            <CardContent className="p-6 text-center">
              <Sparkles className="h-10 w-10 text-yellow-400 mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg mb-2">Unlock More Artwork!</h3>
              <p className="text-purple-200 text-sm mb-4">
                Complete your weekly Bible lessons to unlock beautiful illustrations
              </p>
              <Button 
                onClick={() => navigate(`/christian/child/${childId}`)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="start-lessons-btn"
              >
                Start Learning
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Image Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-4xl bg-slate-900 border-purple-500/30 p-0 overflow-hidden">
          {selectedImage && (
            <>
              <div className="relative">
                <img 
                  src={selectedImage.illustration} 
                  alt={selectedImage.title}
                  className="w-full max-h-[70vh] object-contain bg-black"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={() => navigateViewer('prev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  data-testid="viewer-prev-btn"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => navigateViewer('next')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  data-testid="viewer-next-btn"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4 bg-slate-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedImage.title}</h2>
                    <p className="text-purple-300">Week {selectedImage.week} • {selectedImage.scripture}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(selectedImage.week, selectedImage.favorite);
                      }}
                      className={`border-pink-500/50 ${selectedImage.favorite ? 'bg-pink-500 text-white' : 'text-pink-400 hover:bg-pink-500/20'}`}
                      data-testid="viewer-favorite-btn"
                    >
                      <Heart className={`h-4 w-4 mr-1 ${selectedImage.favorite ? 'fill-current' : ''}`} />
                      {selectedImage.favorite ? 'Saved' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadImage(selectedImage.illustration, selectedImage.title)}
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                      data-testid="viewer-download-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChristianGallery;
